// Renders a seat ticket on a paper-width-sized canvas, then sends the whole
// thing as one ESC/POS raster command. Total typographic control + identical
// render in the browser preview and on paper. No code-page gymnastics —
// Canvas handles every script the system fonts can draw (Latin, Japanese, …).

import { loadImage, packRaster } from "./escpos-raster";

const ESC = 0x1b;
const GS = 0x1d;

const INIT = u8(ESC, 0x40);
const FULL_CUT = u8(GS, 0x56, 0x00);
const FEED_BEFORE_CUT = u8(0x0a, 0x0a, 0x0a, 0x0a, 0x0a); // ~15mm of paper before the cut

export type PaperSize = "80mm" | "58mm";

// Printable dot count for each supported paper width. 80mm = 8 dots/mm × 72mm
// printable = 576; 58mm = 8 dots/mm × 48mm printable = 384.
const PAPER_DOTS: Record<PaperSize, number> = { "80mm": 576, "58mm": 384 };
const REFERENCE_WIDTH = PAPER_DOTS["80mm"];

// Brand fonts — match the Blind Valet design system. Oswald for display,
// Muli for body. Falls back through to system fonts so non-Latin glyphs
// (Japanese, Cyrillic, …) still render via the OS font for the script.
const DISPLAY_STACK = `'Oswald', 'Muli', 'Helvetica Neue', Arial, sans-serif`;
const BODY_STACK = `'Muli', 'Helvetica Neue', Arial, sans-serif`;

const LOGO_URL = "/assets/img/logo-horiz.svg";

export interface SeatTicketPayload {
  /** Club name — prints at the very top of the ticket as the venue header. */
  clubName?: string;
  tournName: string;
  scheduledAt: Date;
  dispName: string;
  table: number;
  seat: number;
  /** Optional custom table name (e.g. "Ballroom 2a"). Renders below the
   *  table number in a smaller font when present. */
  tableName?: string;
  /** Resolved labels — caller passes translate.instant('TABLE') / ('SEAT'). */
  labels: { table: string; seat: string };
  /** Locale for date formatting; defaults to navigator.language. */
  locale?: string;
  /** Paper width. Defaults to 80mm. 58mm uses a proportionally-scaled layout. */
  paperSize?: PaperSize;
}

/**
 * Pixel constants are authored against an 80mm canvas; `s()` shrinks them
 * proportionally for narrower paper. `printWidth` and `hpad` are precomputed
 * because they're hot-pathed in every layout helper.
 */
interface Dims {
  printWidth: number;
  hpad: number;
  logoWidth: number;
  s: (n: number) => number;
}

function makeDims(paperSize: PaperSize): Dims {
  const printWidth = PAPER_DOTS[paperSize];
  const scale = printWidth / REFERENCE_WIDTH;
  const s = (n: number) => Math.round(n * scale);
  return { printWidth, hpad: s(32), logoWidth: s(280), s };
}

export async function buildSeatTicket(p: SeatTicketPayload): Promise<Uint8Array> {
  const canvas = await renderSeatTicketCanvas(p);
  const ctx = canvas.getContext("2d")!;
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const raster = packRaster(canvas.width, canvas.height, data);
  return concat(INIT, raster, FEED_BEFORE_CUT, FULL_CUT);
}

/**
 * Renders the ticket onto a canvas without printing — used for in-browser
 * design preview. The canvas is exactly what the printer would print:
 * `dims.printWidth` dots wide, black on white, 1 dot = 1 printer pixel.
 */
export async function renderSeatTicketCanvas(p: SeatTicketPayload): Promise<HTMLCanvasElement> {
  const dims = makeDims(p.paperSize || "80mm");

  const [logo] = await Promise.all([
    loadImage(LOGO_URL),
    ensureFontsLoaded(),
  ]);

  const measureCanvas = document.createElement("canvas");
  const measure = measureCanvas.getContext("2d")!;

  const blocks = composeLayout(p, logo, measure, dims);
  const totalHeight = blocks.reduce((s, b) => s + b.height, 0);

  const canvas = document.createElement("canvas");
  canvas.width = dims.printWidth;
  canvas.height = totalHeight;
  const ctx = canvas.getContext("2d")!;
  // Leave the canvas transparent: packRaster thresholds by alpha, so only
  // pixels we explicitly draw become ink. A white fill would mark every
  // pixel as opaque and pack as a solid-black bitmap.
  ctx.clearRect(0, 0, dims.printWidth, totalHeight);
  ctx.fillStyle = "black";

  let y = 0;
  for (const block of blocks) {
    block.draw(ctx, y);
    y += block.height;
  }

  return canvas;
}

interface Block {
  height: number;
  draw: (ctx: CanvasRenderingContext2D, y: number) => void;
}

function composeLayout(
  p: SeatTicketPayload,
  logo: HTMLImageElement,
  measure: CanvasRenderingContext2D,
  dims: Dims,
): Block[] {
  const { s } = dims;
  const date = formatDate(p.scheduledAt, p.locale);
  const blocks: Block[] = [spacer(s(8))];
  if (p.clubName) {
    blocks.push(
      centeredText(p.clubName, { fontPx: s(46), weight: 700, family: DISPLAY_STACK, measure, minPx: s(28), dims }),
      spacer(s(20)),
      ruleBlock(dims),
      spacer(s(20)),
    );
  }
  blocks.push(
    centeredText(p.tournName, { fontPx: s(38), weight: 600, family: DISPLAY_STACK, measure, dims }),
    spacer(s(2)),
    centeredText(date, { fontPx: s(26), weight: 400, family: BODY_STACK, measure, letterSpacing: 0.5, dims }),
    spacer(s(28)),
    ruleBlock(dims),
    spacer(s(32)),
    centeredText(p.dispName, { fontPx: s(78), weight: 700, family: DISPLAY_STACK, measure, minPx: s(32), dims }),
    spacer(s(32)),
    ruleBlock(dims),
    spacer(s(28)),
    seatBlock(p, measure, dims),
    spacer(s(40)),
    logoBlock(logo, dims.logoWidth, dims),
    spacer(s(52)),
  );
  return blocks;
}

function spacer(h: number): Block {
  return { height: h, draw: () => {} };
}

function ruleBlock(dims: Dims): Block {
  const thickness = 3;
  return {
    height: thickness,
    draw: (ctx, y) => {
      ctx.fillRect(dims.hpad, y, dims.printWidth - 2 * dims.hpad, thickness);
    },
  };
}

function logoBlock(img: HTMLImageElement, targetW: number, dims: Dims): Block {
  const aspect = img.height / img.width;
  const h = Math.round(targetW * aspect);
  return {
    height: h,
    draw: (ctx, y) => {
      const x = (dims.printWidth - targetW) / 2;
      ctx.drawImage(img, x, y, targetW, h);
    },
  };
}

interface TextOpts {
  fontPx: number;
  weight: number;
  family: string;
  measure: CanvasRenderingContext2D;
  /** Floor for the auto-shrink pass when the string would overflow width. */
  minPx?: number;
  letterSpacing?: number;
  dims: Dims;
}

function centeredText(text: string, opts: TextOpts): Block {
  const { dims } = opts;
  const maxW = dims.printWidth - 2 * dims.hpad;
  const fontPx = pickFontSize(text, maxW, opts);
  const lineHeight = Math.round(fontPx * 1.18);
  return {
    height: lineHeight,
    draw: (ctx, y) => {
      ctx.font = `${opts.weight} ${fontPx}px ${opts.family}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      if (opts.letterSpacing) {
        // CanvasRenderingContext2D.letterSpacing is supported on Chrome 99+.
        (ctx as any).letterSpacing = `${opts.letterSpacing}px`;
      }
      ctx.fillText(text, dims.printWidth / 2, y + lineHeight / 2);
      if (opts.letterSpacing) (ctx as any).letterSpacing = "0px";
    },
  };
}

function pickFontSize(text: string, maxW: number, opts: TextOpts): number {
  opts.measure.font = `${opts.weight} ${opts.fontPx}px ${opts.family}`;
  const natural = opts.measure.measureText(text).width;
  if (natural <= maxW) return opts.fontPx;
  const floor = opts.minPx ?? Math.floor(opts.fontPx * 0.5);
  return Math.max(floor, Math.floor(opts.fontPx * (maxW / natural)));
}

function seatBlock(p: SeatTicketPayload, measure: CanvasRenderingContext2D, dims: Dims): Block {
  const { s } = dims;
  const labelPx = s(26);
  const numberPx = s(130);     // size for digits and short single-line names
  const wrapPx = s(90);         // size when a name needs to wrap to fit
  const minPx = s(32);          // floor when a single word is wider than a column
  const labelLineH = Math.round(labelPx * 1.4);
  const seatLineH = Math.round(numberPx * 1.05);
  const gap = s(6);

  const colW = dims.printWidth / 2;
  const tableX = colW / 2;
  const seatX = colW * 1.5;
  const tableMaxW = colW - dims.hpad;

  // Table side: prefer single-line at full numberPx; fall back to multi-line
  // wrap at wrapPx; shrink only if a single word exceeds the column.
  const tableText = p.tableName || String(p.table);
  const tableLayout = layoutTablePrimary(
    measure,
    tableText,
    numberPx,
    wrapPx,
    minPx,
    tableMaxW,
  );
  const tableLineH = Math.round(tableLayout.fontPx * 1.0);
  const tableHeight = tableLayout.lines.length * tableLineH;

  // Row sizes to the larger side; smaller side is centered vertically.
  const primaryHeight = Math.max(seatLineH, tableHeight);
  const total = labelLineH + gap + primaryHeight;

  const seatText = String(p.seat);

  return {
    height: total,
    draw: (ctx, y) => {
      ctx.textAlign = "center";

      ctx.font = `500 ${labelPx}px ${DISPLAY_STACK}`;
      ctx.textBaseline = "middle";
      (ctx as any).letterSpacing = "2px";
      ctx.fillText(p.labels.table.toUpperCase(), tableX, y + labelLineH / 2);
      ctx.fillText(p.labels.seat.toUpperCase(), seatX, y + labelLineH / 2);
      (ctx as any).letterSpacing = "0px";

      const primaryTop = y + labelLineH + gap;

      // Seat — single line, centered in the primary row.
      ctx.font = `700 ${numberPx}px ${DISPLAY_STACK}`;
      ctx.fillText(seatText, seatX, primaryTop + primaryHeight / 2);

      // Table — possibly multi-line, vertically centered in the primary row.
      ctx.font = `700 ${tableLayout.fontPx}px ${DISPLAY_STACK}`;
      const tableTop = primaryTop + (primaryHeight - tableHeight) / 2;
      tableLayout.lines.forEach((line, i) => {
        ctx.fillText(line, tableX, tableTop + i * tableLineH + tableLineH / 2);
      });
    },
  };
}

/**
 * Decide how to render the table-side primary text within its column.
 * 1. Try a single line at the larger (number) size — fits short names + any digit.
 * 2. Else greedy-wrap word boundaries at the smaller (wrap) size.
 * 3. If a single word still overflows at wrap size, shrink that pass to fit.
 */
function layoutTablePrimary(
  measure: CanvasRenderingContext2D,
  text: string,
  numberPx: number,
  wrapPx: number,
  minPx: number,
  maxW: number,
): { lines: string[]; fontPx: number } {
  measure.font = `700 ${numberPx}px ${DISPLAY_STACK}`;
  if (measure.measureText(text).width <= maxW) {
    return { lines: [text], fontPx: numberPx };
  }

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return { lines: [text], fontPx: numberPx };

  // If the widest single word exceeds the column at wrapPx, shrink so that
  // word fits — wrapping never breaks inside a word.
  let fontPx = wrapPx;
  measure.font = `700 ${fontPx}px ${DISPLAY_STACK}`;
  let longestWordW = 0;
  for (const w of words) {
    longestWordW = Math.max(longestWordW, measure.measureText(w).width);
  }
  if (longestWordW > maxW) {
    fontPx = Math.max(minPx, Math.floor(wrapPx * (maxW / longestWordW)));
    measure.font = `700 ${fontPx}px ${DISPLAY_STACK}`;
  }

  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const tentative = line ? line + " " + w : w;
    if (measure.measureText(tentative).width <= maxW) {
      line = tentative;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return { lines, fontPx };
}

/**
 * Locale-aware human date for the ticket. Exported for unit testing.
 */
export function formatDate(d: Date, locale?: string): string {
  const l = locale || (typeof navigator !== "undefined" ? navigator.language : "en");
  return new Intl.DateTimeFormat(l, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

async function ensureFontsLoaded(): Promise<void> {
  if (typeof document === "undefined" || !(document as any).fonts) return;
  const fonts = (document as any).fonts;
  // Force the brand display + body weights we draw with to actually fetch.
  await Promise.all([
    fonts.load("600 38px Oswald"),
    fonts.load("700 46px Oswald"),
    fonts.load("700 78px Oswald"),
    fonts.load("500 26px Oswald"),
    fonts.load("700 130px Oswald"),
    fonts.load("400 26px Muli"),
  ]).catch(() => { /* fonts may 404; canvas falls back to the next family */ });
}

function u8(...b: number[]): Uint8Array {
  return Uint8Array.from(b);
}

function concat(...parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
